import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, forwardRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ImportLineItem, HighlightState } from '../types';
import PdfToolbar from './PdfToolbar';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
  pdfUrl: string;
  currentPage: number;
  totalPages: number;
  scale: number;
  rotation: number;
  lines: ImportLineItem[];
  highlight: HighlightState;
  onPrevPage: () => void;
  onNextPage: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  onRotate: () => void;
  onHoverLine: (id: string | null) => void;
  onClickLine: (id: string | null) => void;
  onGoToPage: (page: number) => void;
  scrollToLine: (id: string) => void;
}

export interface PdfViewerHandle {
  scrollToPage: (page: number) => void;
}

const PdfViewer = forwardRef<PdfViewerHandle, PdfViewerProps>(({
  pdfUrl,
  currentPage,
  totalPages,
  scale,
  rotation,
  lines,
  highlight,
  onPrevPage,
  onNextPage,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onRotate,
  onHoverLine,
  onClickLine,
  onGoToPage,
  scrollToLine,
}, ref) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const isScrollingTo = useRef(false);
  const scrollTimeoutId = useRef<ReturnType<typeof setTimeout>>();

  // Register page refs
  const registerPageRef = useCallback((pageNum: number, el: HTMLDivElement | null) => {
    if (el) pageRefs.current.set(pageNum, el);
    else pageRefs.current.delete(pageNum);
  }, []);

  // IntersectionObserver to track visible page
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingTo.current) return;
        let bestPage = currentPage;
        let bestRatio = 0;
        entries.forEach((entry) => {
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            const pageNum = Number(entry.target.getAttribute('data-page'));
            if (pageNum) bestPage = pageNum;
          }
        });
        if (bestRatio > 0 && bestPage !== currentPage) {
          onGoToPage(bestPage);
        }
      },
      { root: container, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    pageRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [totalPages, currentPage, onGoToPage]);

  // Scroll to page when currentPage changes via thumbnail click
  const scrollToPage = useCallback((pageNum: number) => {
    const el = pageRefs.current.get(pageNum);
    if (!el) return;
    isScrollingTo.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    clearTimeout(scrollTimeoutId.current);
    scrollTimeoutId.current = setTimeout(() => { isScrollingTo.current = false; }, 600);
  }, []);

  useImperativeHandle(ref, () => ({ scrollToPage }), [scrollToPage]);

  // Lines with regions for overlays
  const linesByPage = useMemo(() => {
    const map = new Map<number, ImportLineItem[]>();
    lines.forEach((l) => {
      if (l.pdfRegion) {
        const arr = map.get(l.pdfRegion.page) || [];
        arr.push(l);
        map.set(l.pdfRegion.page, arr);
      }
    });
    return map;
  }, [lines]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-auto bg-gray-100">
        <Document file={pdfUrl} loading={<PdfLoading />} error={<PdfError />}>
          <div className="flex flex-col items-center py-4 gap-3">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <div
                key={pageNum}
                ref={(el) => registerPageRef(pageNum, el)}
                data-page={pageNum}
                className="relative shadow-md"
              >
                <Page
                  pageNumber={pageNum}
                  scale={scale}
                  rotate={rotation}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
                {/* Overlays for this page */}
                <PageOverlays
                  lines={linesByPage.get(pageNum) || []}
                  highlight={highlight}
                  onHoverLine={onHoverLine}
                  onClickLine={onClickLine}
                  scrollToLine={scrollToLine}
                />
              </div>
            ))}
          </div>
        </Document>
      </div>

      <PdfToolbar
        currentPage={currentPage}
        totalPages={totalPages}
        scale={scale}
        onPrevPage={() => { onPrevPage(); scrollToPage(Math.max(1, currentPage - 1)); }}
        onNextPage={() => { onNextPage(); scrollToPage(Math.min(totalPages, currentPage + 1)); }}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onResetZoom={onResetZoom}
        onRotate={onRotate}
      />
    </div>
  );
});

// ── Overlays for a single page ──
interface PageOverlaysProps {
  lines: ImportLineItem[];
  highlight: HighlightState;
  onHoverLine: (id: string | null) => void;
  onClickLine: (id: string | null) => void;
  scrollToLine: (id: string) => void;
}

const PageOverlays: React.FC<PageOverlaysProps> = ({
  lines,
  highlight,
  onHoverLine,
  onClickLine,
  scrollToLine,
}) => {
  return (
    <>
      {lines.map((line) => {
        const region = line.pdfRegion!;
        if (region.width === 0 && region.height === 0) return null;
        const isHovered = highlight.hoveredLineId === line.id;
        const isActive = highlight.activeLineId === line.id;
        const visible = isHovered || isActive;

        return (
          <div
            key={line.id}
            className={`absolute rounded-sm transition-colors ${
              visible ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'
            } ${
              isActive
                ? 'bg-violet-200/40 border-2 border-violet-400'
                : isHovered
                ? 'bg-violet-100/30 border-2 border-violet-300'
                : ''
            }`}
            style={{
              left: `${region.x}%`,
              top: `${region.y}%`,
              width: `${region.width}%`,
              height: `${region.height}%`,
            }}
            onMouseEnter={() => onHoverLine(line.id)}
            onMouseLeave={() => onHoverLine(null)}
            onClick={() => {
              onClickLine(line.id);
              scrollToLine(line.id);
            }}
          />
        );
      })}
    </>
  );
};

const PdfLoading: React.FC = () => (
  <div className="flex items-center justify-center h-96 text-gray-400 text-sm">
    Chargement du PDF...
  </div>
);

const PdfError: React.FC = () => (
  <div className="flex items-center justify-center h-96 text-red-400 text-sm">
    Impossible de charger le PDF. Vérifiez que le fichier existe dans public/.
  </div>
);

export default PdfViewer;
